import type { UserExtraData } from '@upright/common/iam/users/types';
import type { UnifiedShellAudience } from './unified-shell-audience';

/**
 * The unified shell gate in two parts: whether the user's organisation offers the shell at all
 * (`offered`), and whether the user has it on (`chosen`). The shell renders when both hold. Kept
 * apart so the "New interface" toggle (PLT-7968) can show an unchecked row to someone who turned
 * the shell off, without that row reaching anyone the shell was never offered to.
 *
 * - No audience: neither, and the privilege is not consulted.
 * - Team users: always offered; the `internalAdmin.unifiedShell` View setting is the choice. The
 *   privilege's team-shortcut would answer `true` for them unconditionally, so it is not read.
 * - Everyone else: offered when the organisation holds `USE_UNIFIED_SHELL`, and chosen unless the
 *   user saved an opt-out. `extraData` is validated on read: only an explicit `true` opts out, so
 *   the opt-out can turn the shell off and never on.
 *
 * Pure so the rule is unit-tested; `useUnifiedShell` and `useUnifiedShellToggle` feed it.
 */
export function unifiedShellChoice(args: {
  audience: UnifiedShellAudience;
  isTeam: boolean;
  teamSetting: boolean;
  hasPrivilege: boolean;
  extraData: Partial<UserExtraData> | undefined;
}): { offered: boolean; chosen: boolean } {
  if (args.audience === null) {
    return { offered: false, chosen: false };
  }
  if (args.isTeam) {
    return { offered: true, chosen: args.teamSetting };
  }
  return { offered: args.hasPrivilege, chosen: args.extraData?.unifiedShellOptOut !== true };
}
